<template>
  <form @submit.prevent="save">
    <error-display v-if="errorMessage">{{errorMessage}}</error-display>
    <div class="content">
      <div class="row">
        <input-text name="username" label="E-mail Address" v-model="user.username" :validation="$v.user.username"></input-text>
      </div>
      <div class="row">
        <input-password name="password" label="Password" v-model="user.password" :validation="$v.user.password"></input-password>
      </div>
      <div class="row">
        <input-password name="confirmPassword" label="Confirm Password" v-model="confirmPassword" :validation="$v.confirmPassword"></input-password>
      </div>
      <div class="row">
        <input-text name="name" label="First Name" class="col-xs-6" v-model="user.name" :validation="$v.user.name"></input-text>
        <input-text name="surname" label="Last Name" class="col-xs-6" v-model="user.surname" :validation="$v.user.surname"></input-text>
      </div>
      <div class="row">
        <input-text name="cellularNumber" label="Cellular Number" v-model="user.cellPhoneNumber" :validation="$v.user.cellPhoneNumber"></input-text>
      </div>
      <div class="row">
        <div class="col-xs-4">
          <div class="row">
            <input-switch name="identityType" v-model="user.identityType" :options="identityTypes"></input-switch>
          </div>
        </div>
        <div class="col-xs-8" v-if="user.identityType === 'IDDOC'">
          <div class="row">
            <input-text name="identityNumber" label="Identity Number" v-model="user.idNumber" :validation="$v.user.idNumber"></input-text>
          </div>
        </div>
        <div class="col-xs-8" v-else-if="user.identityType === 'PASSPORT'">
          <div class="row">
            <input-text name="passportNumber" label="Passport Number" v-model="user.passportNumber" :validation="$v.user.passportNumber"></input-text>
            <input-select name="passportCountry" label="Passport Country" v-model="passportCountry" :options="countries" :validation="$v.passportCountry" :loading="loadingCountries"></input-select>
          </div>
        </div>
      </div>
      <div class="row">
        <input-select name="discipline" label="Select Discipline" v-model="discipline" :options="disciplines" :validation="$v.discipline" @change.native="loadSpecialities" :loading="loadingDisciplines"></input-select>
      </div>
      <div class="row" v-if="discipline">
        <input-select name="speciality" label="Select Speciality" v-model="speciality" :options="specialities" :validation="$v.speciality" @change.native="loadGrades" :loading="loadingSpecialities"></input-select>
      </div>
      <div class="row" v-if="speciality">
        <input-select name="grade" label="Select Speciality Grade" v-model="specialityGrade" :options="grades" :validation="$v.specialityGrade" :loading="loadingGrades"></input-select>
      </div>

      <div class="row">
        <input-text name="hpcsaNumber" label="HPCSA Number" v-model="user.hpcsaNumber" :validation="$v.user.hpcsaNumber"></input-text>
      </div>
      <div class="row">
        <input-text name="practiceNumber" label="Practice Number" v-model="user.prNumber" :validation="$v.user.prNumber"></input-text>
      </div>

      <div class="row">
        <input-select name="academicSector" label="Are you in the Academic Sector" v-model="academicSector" :options="academicSectorOptions" :validation="$v.academicSector" @change.native="loadRolesAndInstitutions"></input-select>
      </div>

      <div v-if="inAcademicSector">
        <div class="row">
          <input-select name="institution" label="Select Training Institution" v-model="trainingInstitution" :options="institutions" :validation="$v.trainingInstitution" :loading="loadingInstitutions"></input-select>
        </div>
        <div class="row">
          <input-select name="role" label="Select Role" v-model="role" :options="roles" :validation="$v.role" :loading="loadingRoles"></input-select>
        </div>

        <template v-if="traineeSignup">
          <div class="row">
            <input-text name="traineePostNumber" label="Trainee Post Number" v-model="trainee.traineePostNumber" :validation="$v.trainee.traineePostNumber"></input-text>
          </div>
        </template>
        <template v-if="displayRegistarAppointmentField">
          <div class="row">
            <input-text
              name="appointmentDate"
              label="Date of appointment as registrar"
              v-model="trainee.registrarAppointmentDate"
              type="date"
              :min-date="registrarAppointmentMinDate"
              :max-date="registrarAppointmentMaxDate"
              :validation="$v.trainee.registrarAppointmentDate"
            ></input-text>
          </div>
        </template>

        <div v-else-if="role && role.name === 'SUPERVISOR'">
          <div class="row">
            <input-text name="clinicalUnit" label="Clinical Unit" v-model="supervisor.clinicalUnit" :validation="$v.supervisor.clinicalUnit"></input-text>
          </div>
          <div class="row" v-if="trainingInstitution && speciality">
            <input-text name="hod" label="Head of Department" v-model="hodFullName" @click.native="openHodSearchModal" :validation="$v.hod"></input-text>

            <hod-search-modal
              :open="hodSearchModalOpen"
              @close="closeHodSearchModal"
              :speciality-id="speciality.id"
              :training-institute-id="trainingInstitution.id">
            </hod-search-modal>
          </div>
          <div class="row" v-if="displayHodWarning">
            <div class="col-xs-12 text-center">
              <div class="alert alert-warning">
                The email entered belongs to an HOD in a different training institution or speciality. Click register if you are sure you want to continue sending them the access request.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="footer">
      <div class="row">
        <div class="input-group col-sm-offset-3">
          <input type="checkbox" name="terms" required> I have read and accept the
            <a class="highlight" target="_blank" @click.prevent="openTermsModal">Terms and Conditions</a>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12 text-center">
          <submit-button id="signUpSubmit" :loading="loading">Register</submit-button>
        </div>
      </div>
    </div>

    <academic-terms-modal :open="termsModalOpen" @close="closeTermsModal" id="openTermsModal"></academic-terms-modal>
  </form>
</template>

<script>
  import { required, sameAs, email } from 'vuelidate/lib/validators';
  import { phoneNumber, saIdNumber, requiredIf, password, dateFormat, dateBetween } from 'utils/validators';

  import InputText from 'components/common/inputs/InputText';
  import InputPassword from 'components/common/inputs/InputPassword';
  import InputSelect from 'components/common/inputs/InputSelect';
  import InputSwitch from 'components/common/inputs/InputSwitch';

  import SubmitButton from 'components/common/buttons/SubmitButton';

  import HodSearchModal from 'components/common/modals/HodSearchModal';

  import ErrorDisplay from 'components/common/outputs/ErrorDisplay';

  export default {
    name: 'academic-form',
    components: {
      InputText,
      InputPassword,
      InputSelect,
      InputSwitch,
      SubmitButton,
      HodSearchModal,
      ErrorDisplay,
      AcademicTermsModal: () => import('components/common/modals/AcademicTermsModal'),
    },
    data: () => ({
      loading: false,
      errorMessage: '',
      countries: [],
      disciplines: [],
      specialities: [],
      grades: [],
      roles: [],
      institutions: [],
      user: {
        username: '',
        password: '',
        name: '',
        surname: '',
        cellPhoneNumber: '',
        identityType: 'IDDOC',
        idNumber: '',
        passportNumber: '',
        passportCountryId: '',
        trainingInstitutionId: '',
        disciplineId: '',
        specialityId: '',
        specialityGradeId: '',
        hpcsaNumber: '',
        prNumber: '',
      },
      confirmPassword: '',
      academicSector: undefined,
      passportCountry: undefined,
      trainingInstitution: undefined,
      discipline: undefined,
      speciality: undefined,
      specialityGrade: undefined,
      role: undefined,
      trainee: {
        traineePostNumber: undefined,
        registrarAppointmentDate: undefined,
      },
      registrarAppointmentMinDate: '1950-01-01',
      registrarAppointmentMaxDate: new Date().toLocaleDateString('en-CA'),
      supervisor: {
        hodId: '',
        clinicalUnit: '',
      },
      identityTypes: [
        {
          name: 'ID Document',
          value: 'IDDOC',
        },
        {
          name: 'Passport',
          value: 'PASSPORT',
        },
      ],
      academicSectorOptions: [
        {
          name: 'Yes',
          value: true,
        },
        {
          name: 'No',
          value: false,
        },
      ],
      hodSearchModalOpen: false,
      hod: undefined,
      hodInvite: undefined,
      termsModalOpen: false,
      displayHodWarning: false,
    }),
    validations: {
      user: {
        name: { required },
        surname: { required },
        username: {
          required,
          email,
        },
        cellPhoneNumber: {
          required,
          phoneNumber,
        },
        password: {
          required,
          password,
        },
        idNumber: {
          requiredIf: requiredIf(user => user.identityType === 'IDDOC'),
          saIdNumber,
        },
        passportNumber: {
          requiredIf: requiredIf(user => user.identityType === 'PASSPORT'),
        },
        passportCountryId: {
          requiredIf: requiredIf(user => user.identityType === 'PASSPORT'),
        },
        disciplineId: { required },
        specialityId: { required },
        specialityGradeId: { required },
        hpcsaNumber: { required },
        prNumber: {},
      },
      confirmPassword: {
        required,
        sameAsPassword: sameAs(model => model.user.password),
      },
      passportCountry: {
        requiredIf: requiredIf(data => data.user.identityType === 'PASSPORT'),
      },
      discipline: { required },
      speciality: { required },
      specialityGrade: { required },
      academicSector: { required },
      trainingInstitution: {
        requiredIf: requiredIf(function isInAcademicSector() {
          return this.inAcademicSector;
        }),
      },
      role: {
        requiredIf: requiredIf(function isInAcademicSector() {
          return this.inAcademicSector;
        }),
      },
      trainee: {
        traineePostNumber: {},
        registrarAppointmentDate: {
          dateFormat,
          dateBetween: dateBetween(
            '1950-01-01',
            new Date().toLocaleDateString('en-CA'),
          ),
        },
      },
      supervisor: {
        hodId: {},
        clinicalUnit: {
          requiredIf: requiredIf(function () {
            return this.supervisorSignup;
          }),
        },
      },
      hod: {
        requiredIf: requiredIf(function () {
          return this.supervisorSignup && !this.hodInvite;
        }),
      },
      hodInvite: {
        requiredIf: requiredIf(function () {
          return this.supervisorSignup && !this.hod;
        }),
      },
    },
    computed: {
      hodFullName() {
        if (this.hod) {
          return `${this.hod.user.name} ${this.hod.user.surname}`;
        } else if (this.hodInvite) {
          return this.hodInvite.name;
        }

        return '';
      },
      supervisorSignup() {
        return this.role && this.role.name === 'SUPERVISOR';
      },
      traineeSignup() {
        return this.role && this.role.name === 'TRAINEE';
      },
      inAcademicSector() {
        return this.academicSector && this.academicSector.value;
      },
      loadingCountries() {
        return this.countries.length === 0;
      },
      loadingDisciplines() {
        return this.disciplines.length === 0;
      },
      loadingSpecialities() {
        return this.specialities.length === 0;
      },
      loadingGrades() {
        return this.grades.length === 0;
      },
      loadingRoles() {
        return this.roles.length === 0;
      },
      loadingInstitutions() {
        return this.institutions.length === 0;
      },
      displayRegistarAppointmentField() {
        return this.traineeSignup
          && this.specialityGrade
          && this.specialityGrade.name === 'Registrar';
      },
    },
    methods: {
      async loadCountries() {
        if (this.countries.length === 0) {
          try {
            this.countries = await this.$api.lookupLogbookCountries();
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      async loadDisciplines() {
        if (this.disciplines.length === 0) {
          try {
            const disciplines = await this.$api.lookupDisciplines();
            this.disciplines = disciplines.filter(discipline => discipline.active);
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      async loadSpecialities() {
        if (this.discipline) {
          this.speciality = undefined;
          this.specialityGrade = undefined;

          try {
            const specialities = await this.$api.lookupSpecialities(this.discipline.id);
            this.specialities = specialities.filter(speciality => speciality.active);
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      async loadGrades() {
        if (this.speciality) {
          this.specialityGrade = undefined;

          try {
            this.grades = await this.$api.lookupGrades(this.speciality.id);
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      async loadRolesAndInstitutions() {
        if (this.roles.length === 0) {
          try {
            this.institutions = await this.$api.lookupInstitutions();
            const roles = await this.$api.lookupRolesAcademic();
            this.roles = roles.filter(role => role !== 'HCP').map(role => ({ name: role }));
          } catch (error) {
            this.errorMessage = this.$api.processError(error);
          }
        }
      },
      clearIdentificationData() {
        switch (this.user.identityType) {
          case 'IDDOC':
            this.user.passportNumber = '';
            this.user.passportCountryId = '';
            this.passportCountry = undefined;
            break;
          case 'PASSPORT':
            this.user.idNumber = '';
            break;
          default:
            break;
        }
      },
      async save() {
        this.user = Object.assign(this.user, {
          passportCountryId: this.passportCountry ? this.passportCountry.id : undefined,
          trainingInstitutionId: this.trainingInstitution ? this.trainingInstitution.id : undefined,
          disciplineId: this.discipline.id,
          specialityId: this.speciality.id,
          specialityGradeId: this.specialityGrade.id,
        });

        let signupRole;

        if (this.academicSector.value) {
          signupRole = this.role.name;

          switch (this.role.name) {
            case 'TRAINEE':
              this.user = Object.assign(this.user, this.trainee);
              break;
            case 'SUPERVISOR':
              this.user = Object.assign(this.user, {
                hodId: this.hod ? this.hod.id : undefined,
                clinicalUnit: this.supervisor.clinicalUnit,
                hodInvite: this.hodInvite,
                confirmedHod: this.displayHodWarning,
              });
              break;
            default:
              break;
          }
        } else {
          signupRole = 'HCP';
        }

        this.$v.$touch();

        if (this.$v.$invalid) {
          this.errorMessage = this.$api.validationError;
          window.scrollTo(0, 0);
        } else {
          this.clearIdentificationData();

          try {
            this.loading = true;

            if (this.$route.query.inviteKey) {
              await this.$api.register(this.user, signupRole, this.$route.query.inviteKey);
            } else {
              await this.$api.register(this.user, signupRole);
            }

            await this.$api.login({
              username: this.user.username,
              password: this.user.password,
            });
          } catch (error) {
            if (error.toString().includes('406')) {
              this.displayHodWarning = true;
            } else {
              this.errorMessage = this.$api.processError(error);
              window.scrollTo(0, 0);
            }
          } finally {
            this.loading = false;
          }
        }
      },
      openHodSearchModal() {
        this.hodSearchModalOpen = true;
      },
      closeHodSearchModal(value) {
        this.hod = value.hod;
        this.hodInvite = value.hodInvite;

        this.hodSearchModalOpen = false;
      },
      openTermsModal() {
        this.termsModalOpen = true;
      },
      closeTermsModal() {
        this.termsModalOpen = false;
      },
    },
    async mounted() {
      await this.loadCountries();
      await this.loadDisciplines();

      if (this.$route.query.inviteKey) {
        try {
          const inviteData = await this.$api.fetchInviteData(this.$route.query.inviteKey);

          this.user.username = inviteData.emailAddress;
          this.user.name = inviteData.name;
          this.academicSector = this.academicSectorOptions.find(option => option.value === true);
          await this.loadRolesAndInstitutions();
          this.role = this.roles.find(role => role.name === inviteData.role);
        } catch (error) {
          if (error.response) {
            this.errorMessage = 'Unable to retrieve invite data';
          } else {
            this.errorMessage = this.$api.communicationError;
          }
        }
      }
    },
  };

</script>



// WEBPACK FOOTER //
// AcademicForm.vue?e30c575c